<template>
  <div id="product-reviews" class="space-y-12">

    <div class="flex items-center justify-between">
      <div>
            <h2 class="text-2xl font-bold text-gray-900">{{ $t('Customer reviews') }} ({{ rating.count }})</h2>

  <ProductReviewModal
        v-model:open="reviewModalOpen"
        :productId="productId"
        @review:create="handleNewReview($event)"
      ><UButton class="p-0 cursor-pointer" :label="$t('Write a review')" @click="openWriteReview" variant="link" /></ProductReviewModal>
      </div>
   
   <UCard class="w-full max-w-lg">
      <div class="flex items-center gap-6">
        <div class="text-center flex flex-col items-center">
          <Icon name="i-heroicons-star-solid" class="w-8 h-8 text-yellow-400" />

          <i18n-t keypath="{n} out of {m}">
            <template #n>
              <span class="text-5xl font-bold text-gray-900">
                {{ rating.average }}
              </span>
            </template>
            <template #m>5</template>
          </i18n-t>
        </div>
        <USeparator orientation="vertical" />
        <div class="flex-1 space-y-2">
          <div v-for="star in 5" :key="star" class="flex items-center gap-3">
            <span class="text-sm text-gray-600  text-nowrap">{{ star }}</span>
            <div class="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
              <div class="h-full bg-yellow-400" :style="`width: ${distributionPercentage[star]}%`"></div>
            </div>
            <span v-if="reviewCount[star]" class="text-sm text-gray-600  text-nowrap">{{ $t('[x] reviews').replace('[x]', reviewCount[star].toString()) }}</span>
          </div>
        </div>
      </div>
    </UCard>
     </div>

    <div v-if="pending" class="space-y-4">
      <USkeleton v-for="n in 2" :key="n" class="h-24" />
    </div>
      <UAlert v-else-if="error" color="error">
      {{ error.message || $t('Failed to load reviews.') }}
    </UAlert>
    <div v-else class="space-y-4">
      <ProductReviewCard @review:deleted="handleReviewDeleted" :highlight="latestReviewId === review.id" v-for="review in reviews" :key="review.id" :review="review" />
    </div>
    
    <UButton
      v-if="hasMore"
      :label="$t('Load more')"
      :disabled="pending"
      @click="loadMore"
    />
  </div>
</template>

<script setup lang="ts">
import type { ProductRating, ProductReview } from '~/types'
import ProductReviewCard from '~/components/molecules/ProductReviewCard.vue'
import ProductReviewModal from '~/components/organisms/ProductReviewModal.vue';

const props = defineProps<{
  productId: number,
  rating: ProductRating
}>()

const {
  perPage
} = useRuntimeConfig();

const distributionPercentage = computed(() => {
  return Object.fromEntries(
    [1,2,3,4,5].map(star => {
      const count = props.rating.distribution[`star${star}`] || 0;
      const total = props.rating.count || 1;
      const percentage = (count / total) * 100;
      return [star, percentage.toFixed(2)];
    })
  ) as Record<number, string>;
})

const hasMore = ref(false);

const catalogService = useCatalogService();

const query = ref<{ productId: number; page: number; perPage: number }>({
  productId: props.productId,
  page: 1,
  perPage
});

const {
  data,
  pending,
  error
} = useAsyncData(
    () => catalogService.getProductReviews(query.value),
    {
      watch: [query],
      default: () => ({ data: [], meta: { currentPage: 0, total: 0, rating: 0, lastPage: 0 } }),
      server: false
    }
);

const reviews = ref<ProductReview[]>([]);

watchEffect(() => {
  reviews.value = [...reviews.value, ...data.value.data];
  reviews.value = Array.from(new Map(reviews.value.map(r => [r.id, r])).values());
  hasMore.value = data.value.meta.lastPage > query.value.page;
})

const loadMore = () => {
  query.value.page += 1;
}

const reviewModalOpen = ref(false);

const openWriteReview = () => {
  reviewModalOpen.value = true;
}

const latestReviewId = ref<number | null>(null);

const handleNewReview = (review: ProductReview) => {
  latestReviewId.value = review.id;
  reviews.value = [...reviews.value, review];
};


const handleReviewDeleted = (reviewId: number) => {
  reviews.value = reviews.value.filter(r => r.id !== reviewId);
};

const reviewCount = computed(() => {
  const distribution : Record<number, number> = {}

  for (let i = 1; i <=5; i++) {
    distribution[i] = props.rating.distribution[`star${i}`] || 0;
  }

  return distribution
})
</script>
